/**
 * Best-effort device location capture for the GEO-STAMP of an
 * evidence photo (proof-of-presence).
 *
 * Design rules:
 * · NEVER blocks upload: if permission is denied, no GPS, or timeout
 *   expires, resolves to `null` and photo uploads without stamp (NULL columns).
 * · Never throws: any error is swallowed and returns `null`.
 * · Returns only what the domain persists (lat/lng/accuracy); remainder of
 *   `GeolocationPosition` is discarded.
 * · NO position CACHE (`maximumAge: 0`). This rule keeps the
 *   geo-stamp honest, and is not cosmetic: with caching (previously 30 s)
 *   the browser serves the last known position WITHOUT re-checking permission,
 *   so — measured in e2e/geo-stamp — a photo taken after REVOKING location
 *   permission was still stamped with the previous coordinate. Two flaws
 *   in one for a compliance product: (1) location attached after consent
 *   is withdrawn, and (2) stamp claims "photo was taken here" when it could
 *   be from 30 seconds ago. Capture runs in parallel with upload (see
 *   photo-strip) and never blocks it, so requesting fresh position never
 *   penalizes evidence: at worst there is no stamp.
 */

export interface CapturedGeo {
  latitude: number;
  longitude: number;
  /** Accuracy radius in meters (or null if device does not report it). */
  geoAccuracy: number | null;
}

const DEFAULT_TIMEOUT_MS = 8000;

/**
 * Maximum accepted age for position. 0 = always a fresh reading
 * (see header note: browser cache skips permission check and stamps
 * photos with expired coordinates).
 */
const DEFAULT_MAXIMUM_AGE_MS = 0;

/**
 * Attempts to get current position. Resolves to coordinate or `null`
 * (permission denied / unsupported / timeout / error). Never rejects.
 */
export function captureGeolocation(
  options: { timeoutMs?: number; maximumAgeMs?: number } = {},
): Promise<CapturedGeo | null> {
  const geo =
    typeof navigator !== "undefined" && "geolocation" in navigator
      ? navigator.geolocation
      : undefined;
  if (!geo) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    let settled = false;
    const done = (value: CapturedGeo | null) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(value);
    };

    try {
      geo.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords;
          if (
            typeof latitude !== "number" ||
            typeof longitude !== "number" ||
            !Number.isFinite(latitude) ||
            !Number.isFinite(longitude)
          ) {
            done(null);
            return;
          }
          done({
            latitude,
            longitude,
            geoAccuracy:
              typeof accuracy === "number" && Number.isFinite(accuracy) && accuracy >= 0
                ? accuracy
                : null,
          });
        },
        () => {
          done(null);
        },
        {
          enableHighAccuracy: true,
          timeout: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
          maximumAge: options.maximumAgeMs ?? DEFAULT_MAXIMUM_AGE_MS,
        },
      );
    } catch {
      done(null);
    }
  });
}
